import React from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import {
  FormikWrapper,
  FormCheckbox,
  Button,
} from '@uncinc/uncinc-react-kitchen-sink';
import AddressFields from '../AddressFields';

import type { UserProfileAddress } from '../../types/user';
import { useDialogDrawerContext } from '../../context/DialogDrawerContext';
import { DEFAULT_COUNTRY_CODE } from '../../config/constants';

import './style.scss';

const FormFields = ({
  showActions = false,
  allowSetDefault = true,
  allowSetDefaultBilling = true,
  onFormCancel = () => {},
}) => {
  const { t } = useTranslation();

  return (
    <>
      <AddressFields
        showOrganisation
        organisationRequired
        organisationLabel={t('address.organization_name')}
      />
      {allowSetDefault && (
        <div className="row">
          <div className="col-12">
            <div className="WebformCheckboxes">
              <FormCheckbox
                id={v4()}
                fieldName="set_default"
                title={t('address.default_address')}
              />
            </div>
          </div>
        </div>
      )}
      {allowSetDefaultBilling && (
        <div className="row">
          <div className="col-12">
            <div className="WebformCheckboxes">
              <FormCheckbox
                id={v4()}
                fieldName="set_default_billing"
                title={t('address.default_billing_address')}
              />
            </div>
          </div>
        </div>
      )}
      {showActions && (
        <div className="row">
          <div className="col-6">
            <Button className="Button--block" type="submit">
              {t('general.save')}
            </Button>
          </div>
          <div className="col-6">
            <Button
              onClick={onFormCancel}
              className="Button Button--block Button--inverted"
            >
              {t('general.cancel')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export interface AddressFormProps {
  address?: UserProfileAddress;
  isDefault?: boolean;
  isDefaultBilling?: boolean;
  hideTitle?: boolean;
  showActions?: boolean;
  fieldsOnly?: boolean;
  allowSetDefault?: boolean;
  allowSetDefaultBilling?: boolean;
  onSubmit?: Function;
  onCancel?: Function;
}

const AddressForm: React.FC<AddressFormProps> = ({
  address,
  isDefault = false,
  isDefaultBilling = false,
  hideTitle = false,
  fieldsOnly = false,
  allowSetDefault = true,
  allowSetDefaultBilling = true,
  showActions = true,
  onSubmit = () => {},
  onCancel = () => {},
}) => {
  const { t } = useTranslation();
  const { closeDialog } = useDialogDrawerContext();
  let initialValues = {
    country_code: DEFAULT_COUNTRY_CODE,
    organization: '',
    given_name: '',
    family_name: '',
    postal_code: '',
    address_line1: '',
    address_line2: '',
    locality: '',
    set_default: isDefault,
    set_default_billing: isDefaultBilling,
  };
  // If an addresss was passed, add the data to the initialValues.
  if (address) {
    initialValues = {
      ...initialValues,
      ...address,
    };
  }

  // Set up the validation.
  const AddressValidationSchema = Yup.object().shape({
    country_code: Yup.string(),
    set_default: Yup.string(),
    set_default_billing: Yup.string(),
    given_name: Yup.string().required(t('address.given_name_required_error')),
    family_name: Yup.string().required(t('address.family_name_required_error')),
    organization: Yup.string()
      .nullable()
      .trim()
      .required(t('address.organization_required_error')),
    locality: Yup.string().required(t('address.locality_required_error')),
    postal_code: Yup.string()
      .required(t('address.postal_code_required_error'))
      .matches(
        /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-zA-Z ._-]{2}$/,
        t('address.postal_code_invalid_error'),
      ),
    address_line1: Yup.string().required(
      t('address.address_line1_required_error'),
    ),
    address_line2: Yup.string()
      .trim()
      .required(t('address.address_line2_required_error'))
      .matches(/^(?:[0-9]{1})/, t('address.house_number_invalid_error')),
  });

  // On form submit.
  const onFormSubmit = (data: any) => {
    // Close the drawer before dispatching: the profile update re-renders the
    // address list, and re-rendering while the drawer is still open remounts
    // it — a visible re-animate. Closing first lets it play one clean exit.
    closeDialog();
    onSubmit({
      ...data,
      postal_code: data.postal_code.replace(' ', ''),
    });
  };

  // On form cancel.
  const onFormCancel = () => {
    onCancel();
    closeDialog();
  };

  return (
    <div className="AddressForm">
      {!hideTitle && <h2>{t('address.title')}</h2>}
      {fieldsOnly ? (
        <FormFields
          showActions={showActions}
          onFormCancel={onFormCancel}
          allowSetDefault={allowSetDefault}
          allowSetDefaultBilling={allowSetDefaultBilling}
        />
      ) : (
        <FormikWrapper
          onSubmit={onFormSubmit}
          initialValues={initialValues}
          validationSchema={AddressValidationSchema}
        >
          <FormFields
            showActions={showActions}
            onFormCancel={onFormCancel}
            allowSetDefault={allowSetDefault}
            allowSetDefaultBilling={allowSetDefaultBilling}
          />
        </FormikWrapper>
      )}
    </div>
  );
};

export default AddressForm;
